import { Component } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import ErrorMessage from '../common/ErrorMessage';
import Container from '../layout/Container';
import './ErrorBoundary.css';

// Route error component for React Router
export function RouteErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <Container className="error-boundary">
          <div className="error-boundary-content">
            <h1 className="error-boundary-title">404 - Page Not Found</h1>
            <p className="error-boundary-message">
              The page you're looking for doesn't exist.
            </p>
            <div className="error-boundary-actions">
              <a href="/" className="error-boundary-link">
                Go to Home
              </a>
              <a href="/competitions" className="error-boundary-link">
                Browse Competitions
              </a>
            </div>
          </div>
        </Container>
      );
    }

    return (
      <Container className="error-boundary">
        <div className="error-boundary-content">
          <h1 className="error-boundary-title">
            {error.status} - {error.statusText}
          </h1>
          <p className="error-boundary-message">
            {error.data?.message || 'An error occurred while loading this page.'}
          </p>
          <div className="error-boundary-actions">
            <a href="/" className="error-boundary-link">
              Go to Home
            </a>
            <button 
              onClick={() => window.location.reload()} 
              className="error-boundary-button"
            >
              Reload Page
            </button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="error-boundary">
      <ErrorMessage
        title="Something went wrong"
        message={error?.message || 'An unexpected error occurred'}
        onRetry={() => window.location.reload()}
      />
    </Container>
  );
}

// Class-based error boundary for catching React errors
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Container className="error-boundary">
          <ErrorMessage
            title="Something went wrong"
            message={this.state.error?.message || 'An unexpected error occurred'}
            onRetry={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
          />
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
